import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit, Trash2, Tag } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './Inventory.css';

const BASE_CATEGORIES = [
    { value: 'bebidas', label: 'Bebidas' },
    { value: 'productos_capilares', label: 'Productos Capilares' },
];

// Clave localStorage para categorías creadas pero aún sin producto guardado
const PENDING_KEY = 'inventory_pending_categories';

const getPendingCategories = () => {
    try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]'); } catch { return []; }
};
const setPendingCategories = (cats) => {
    localStorage.setItem(PENDING_KEY, JSON.stringify(cats));
};

// Genera el value (slug) a partir del label
const toSlug = (label) =>
    label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const Inventory = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editingProduct, setEditingProduct] = useState(null);
    // customCategories: unión de categorías de la BD + pendientes en localStorage
    const [customCategories, setCustomCategories] = useState([]);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'bebidas',
        price: '',
        stock: '',
        stock_vitrina: '',
        min_stock: '',
        image_url: '',
        active: true
    });

    // Categorías base internas (sin 'all')
    const baseValues = new Set(BASE_CATEGORIES.map(c => c.value));

    // Todas las categorías disponibles para el select
    const allCategories = [
        ...BASE_CATEGORIES,
        ...customCategories,
    ];

    const loadCustomCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/admin/inventory/categories`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                // La API devuelve array de strings con los valores DISTINCT
                const values = await res.json(); // ['bebidas', 'productos_capilares', 'aceites_barba', ...]
                // Filtrar los que no son base y convertir a {value, label}
                const fromDB = values
                    .filter(v => !baseValues.has(v))
                    .map(v => ({ value: v, label: v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }));

                // Unir con los pendientes en localStorage (pueden tener label original)
                const pending = getPendingCategories().filter(p => !values.includes(p.value));
                // Limpiar pendientes que ya llegaron a la BD
                const pendingStillPending = getPendingCategories().filter(p => !values.includes(p.value));
                setPendingCategories(pendingStillPending);

                // Merge: primero los de BD (tienen label capitalizado), luego pendientes
                const merged = [...fromDB];
                for (const p of pendingStillPending) {
                    if (!merged.some(m => m.value === p.value)) merged.push(p);
                }
                setCustomCategories(merged);
            }
        } catch (err) {
            // Si falla la API, al menos mostramos los pendientes de localStorage
            const pending = getPendingCategories();
            setCustomCategories(pending);
            console.error('Error cargando categorías:', err);
        }
    };

    const handleCreateCategory = () => {
        const trimmed = newCategoryName.trim();
        if (!trimmed) return;
        const value = toSlug(trimmed);
        const alreadyExists = allCategories.some(
            c => c.value === value || c.label.toLowerCase() === trimmed.toLowerCase()
        );
        if (alreadyExists) {
            alert('Ya existe una categoría con ese nombre.');
            return;
        }
        const newCat = { value, label: trimmed };
        // Guardar en localStorage como "pendiente" hasta que se cree un producto con ella
        setPendingCategories([...getPendingCategories(), newCat]);
        setCustomCategories(prev => [...prev, newCat]);
        setFormData(prev => ({ ...prev, category: value }));
        setNewCategoryName('');
        setShowNewCategoryInput(false);
    };

    const handleDeleteCustomCategory = (value) => {
        if (!window.confirm('¿Quitar esta categoría de la lista?')) return;
        // Solo se elimina de la vista y de los pendientes; los productos en BD mantienen su category
        setPendingCategories(getPendingCategories().filter(p => p.value !== value));
        setCustomCategories(prev => prev.filter(c => c.value !== value));
        if (formData.category === value) {
            setFormData(prev => ({ ...prev, category: 'bebidas' }));
        }
    };

    useEffect(() => {
        loadProducts();
        loadCustomCategories();
    }, []);

    const loadProducts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/inventory`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setProducts(data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.price || formData.stock === '') {
            alert('Por favor completa los campos requeridos');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingProduct 
                ? `${API_BASE_URL}/admin/inventory/${editingProduct.id}`
                : `${API_BASE_URL}/admin/inventory`;
            
            const method = editingProduct ? 'PUT' : 'POST';
            
            const payload = {
                ...formData,
                price: parseFloat(formData.price),
                stock: parseInt(formData.stock),
                stock_vitrina: formData.stock_vitrina !== '' ? parseInt(formData.stock_vitrina) || 0 : 0,
                min_stock: formData.min_stock ? parseInt(formData.min_stock) : 0
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                loadProducts();
                setShowModal(false);
                setEditingProduct(null);
                setFormData({
                    name: '', description: '', category: 'bebidas', price: '',
                    stock: '', stock_vitrina: '', min_stock: '', image_url: '', active: true
                });
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al guardar el producto');
            }
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            description: product.description || '',
            category: product.category,
            price: product.price.toString(),
            stock: product.stock.toString(),
            stock_vitrina: (product.stock_vitrina ?? 0).toString(),
            min_stock: product.min_stock?.toString() || '',
            image_url: product.image_url || '',
            active: product.active
        });
        setShowModal(true);
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/inventory/${productId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                loadProducts();
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al eliminar el producto');
            }
        } catch (error) {
            console.error('Error deleting product:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const getCategoryLabel = (category) => {
        const found = allCategories.find(c => c.value === category);
        if (found) return found.label;
        return category;
    };

    const getStockStatus = (stock, minStock) => {
        if (stock <= 0) return { class: 'stock-out', text: 'Sin Stock' };
        if (stock <= minStock) return { class: 'stock-low', text: 'Stock Bajo' };
        return { class: 'stock-ok', text: 'Disponible' };
    };


    return (
        <div className="tab-content">
            <div className="inventory-header">
                <div>
                    <h2>Gestión de Inventario</h2>
                    <p className="inventory-subtitle">Administra productos, bebidas y suministros</p>
                </div>
                <button className="add-barber-btn" onClick={() => {
                    setEditingProduct(null);
                    setFormData({
                        name: '', description: '', category: 'bebidas', price: '',
                        stock: '', stock_vitrina: '', min_stock: '', image_url: '', active: true
                    });
                    setShowModal(true);
                }}>
                    <Plus size={20} />
                    Nuevo Producto
                </button>
            </div>

            {!loading && products.length > 0 && (
                <div className="inventory-filter-bar">
                    <label htmlFor="inventory-category-filter" className="inventory-filter-label">
                        Filtrar por categoría:
                    </label>
                    <select
                        id="inventory-category-filter"
                        className="inventory-filter-select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                        <option value="all">Todas las categorías</option>
                        {allCategories.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner" />
                </div>
            ) : products.length === 0 ? (
                <div className="empty-state">
                    <Package size={48} />
                    <p>No hay productos en el inventario</p>
                </div>
            ) : (() => {
                const filteredProducts = categoryFilter === 'all'
                    ? products
                    : products.filter(p => p.category === categoryFilter);
                return filteredProducts.length === 0 ? (
                    <div className="empty-state">
                        <Package size={48} />
                        <p>No hay productos en esta categoría</p>
                    </div>
                ) : (
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Categoría</th>
                                <th>Precio</th>
                                <th>Stock</th>
                                <th>Stock Vitrina / Mostrador</th>
                                <th>Stock Mínimo</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.map(product => {
                                const stockStatus = getStockStatus(product.stock, product.min_stock || 0);
                                return (
                                    <tr key={product.id}>
                                        <td>
                                            <div className="product-name-cell">
                                                {product.image_url && (
                                                    <img 
                                                        src={product.image_url} 
                                                        alt={product.name}
                                                        className="product-table-image"
                                                    />
                                                )}
                                                <div>
                                                    <div className="product-name">{product.name}</div>
                                                    {product.description && (
                                                        <div className="product-description-table">
                                                            {product.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="category-badge">
                                                {getCategoryLabel(product.category)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="price-value">
                                                {formatCurrency(product.price)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="stock-value">{product.stock}</span>
                                        </td>
                                        <td>
                                            <span className="vitrina-stock-value">{product.stock_vitrina ?? 0}</span>
                                        </td>
                                        <td>
                                            <span className="min-stock-value">{product.min_stock || 0}</span>
                                        </td>
                                        <td>
                                            <span className={`stock-badge ${stockStatus.class}`}>
                                                {stockStatus.text}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button 
                                                    onClick={() => handleEdit(product)} 
                                                    className="table-action-btn edit"
                                                    title="Editar"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(product.id)} 
                                                    className="table-action-btn delete"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                );
            })()}

            {showModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                }}>
                    <div className="modal inventory-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="inventory-modal-header" style={{ 
                            backgroundColor: '#ef4444', 
                            color: 'white', 
                            padding: '1.5rem', 
                            borderRadius: '8px 8px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0 }}>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</h2>
                            <button 
                                onClick={() => {
                                    setShowModal(false);
                                    setEditingProduct(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="inventory-modal-content" style={{ padding: '2rem' }}>
                            <div className="inventory-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Nombre del Producto *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="Ej: Coca Cola 350ml"
                                />
                            </div>
                            <div className="inventory-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Descripción
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        minHeight: '80px',
                                        resize: 'vertical'
                                    }}
                                    placeholder="Descripción del producto"
                                />
                            </div>
                            <div className="inventory-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div className="inventory-form-field">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Categoría *
                                    </label>
                                    <select
                                        value={showNewCategoryInput ? '__nueva__' : formData.category}
                                        onChange={(e) => {
                                            if (e.target.value === '__nueva__') {
                                                setShowNewCategoryInput(true);
                                                setNewCategoryName('');
                                            } else {
                                                setShowNewCategoryInput(false);
                                                setFormData({ ...formData, category: e.target.value });
                                            }
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        {allCategories.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                        <option value="__nueva__">➕ Crear nueva categoría...</option>
                                    </select>

                                    {/* Input para nueva categoría */}
                                    {showNewCategoryInput && (
                                        <div style={{ marginTop: '0.65rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                autoFocus
                                                value={newCategoryName}
                                                onChange={(e) => setNewCategoryName(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCategory(); }}
                                                placeholder="Nombre de la categoría"
                                                style={{
                                                    flex: 1,
                                                    padding: '0.6rem 0.75rem',
                                                    border: '1.5px solid #ef4444',
                                                    borderRadius: '6px',
                                                    fontSize: '0.95rem',
                                                    outline: 'none'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleCreateCategory}
                                                style={{
                                                    padding: '0.6rem 1rem',
                                                    background: '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontWeight: '600',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                Crear
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowNewCategoryInput(false);
                                                    setNewCategoryName('');
                                                    setFormData(prev => ({ ...prev, category: allCategories[0]?.value || 'bebidas' }));
                                                }}
                                                style={{
                                                    padding: '0.6rem 0.75rem',
                                                    background: '#f3f4f6',
                                                    color: '#374151',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}

                                    {/* Lista de categorías personalizadas con opción de eliminar */}
                                    {customCategories.length > 0 && (
                                        <div style={{ marginTop: '0.6rem' }}>
                                            <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '0.4rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Mis categorías
                                            </p>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                                {customCategories.map(c => (
                                                    <span
                                                        key={c.value}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '0.3rem',
                                                            padding: '0.25rem 0.6rem',
                                                            background: formData.category === c.value ? '#fef2f2' : '#f9fafb',
                                                            border: `1.5px solid ${formData.category === c.value ? '#ef4444' : '#e5e7eb'}`,
                                                            borderRadius: '999px',
                                                            fontSize: '0.8rem',
                                                            color: formData.category === c.value ? '#ef4444' : '#6b7280',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => { setShowNewCategoryInput(false); setFormData(prev => ({ ...prev, category: c.value })); }}
                                                    >
                                                        <Tag size={11} />
                                                        {c.label}
                                                        <span
                                                            title="Eliminar categoría"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteCustomCategory(c.value); }}
                                                            style={{ marginLeft: '0.1rem', color: '#d1d5db', fontWeight: '700', lineHeight: 1 }}
                                                        >×</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="inventory-form-field">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Precio *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="inventory-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div className="inventory-form-field">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Stock Actual *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.stock}
                                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="inventory-form-field">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Stock Vitrina / Mostrador
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.stock_vitrina}
                                        onChange={(e) => setFormData({ ...formData, stock_vitrina: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="0"
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Parte del stock total en vitrina</small>
                                </div>
                            </div>
                            <div className="inventory-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Stock Mínimo
                                </label>
                                <input
                                    type="number"
                                    value={formData.min_stock}
                                    onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="0"
                                />
                            </div>
                            <div className="inventory-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    URL de Imagen
                                </label>
                                <input
                                    type="text"
                                    value={formData.image_url}
                                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="https://ejemplo.com/imagen.jpg"
                                />
                            </div>
                            <div className="inventory-form-field" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                    id="product-active"
                                />
                                <label htmlFor="product-active" style={{ fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                    Producto activo
                                </label>
                            </div>
                            <div className="inventory-modal-actions" style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        setEditingProduct(null);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSave}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    {editingProduct ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inventory;

